import { Alert, Link, Typography } from "@mui/material";
import { phoneFmt } from "../../formatters";

/*
 * Dev central must not send a rep's patient to the production portal, so the
 * host is per-environment like every other URL this app hands out. The fallback
 * exists because a missing var here renders as instructions to a real patient,
 * and "undefined" is a worse outcome than a stale-but-valid host.
 */
const PORTAL_URL =
  process.env.REACT_APP_PATIENT_PORTAL_URL || "https://patients.wrs.us";

/*
 * The portal accepts either identity at its login tab and mails or texts a code.
 * Both are `NOT NULL` but may be the empty string, and an empty one is exactly a
 * patient who cannot log in that way — so a blank has to drop out of the list
 * rather than be read aloud to the patient as a login.
 */
function loginSentence(email, phone) {
  const identities = [email, phoneFmt(phone)].filter(Boolean);

  if (identities.length === 0) return null;
  if (identities.length === 1) return `They can log in with ${identities[0]}.`;

  return `They can log in with either ${identities[0]} or ${identities[1]}.`;
}

/**
 * What the cash-pay patient still owes the process, in one message.
 *
 * `portal` is `patient_portal` off the treatment payload, and is null for
 * work-comp — where neither an agreement nor a patient payment is part of the
 * flow, so there is nothing to say. Deliberately one alert rather than one per
 * condition: a rep making a call needs the whole ask in a single read, and two
 * stacked warnings invite doing half the job.
 */
function PatientPortalStatus({ portal, email, phone }) {
  if (!portal) return null;

  const needsAgreement = !portal.rental_agreement_signed;
  const needsPayment = portal.payment_state === "unpaid";

  /*
   * The webhook gap: the patient has paid and the money has not landed on an
   * order line yet. Not `unpaid` — a rep chasing the patient here is chasing
   * nothing — but not settled either, so it does not read as done.
   */
  const paymentPending = portal.payment_state === "pending";

  if (!needsAgreement && !needsPayment) {
    return (
      <Alert severity={paymentPending ? "info" : "success"}>
        {paymentPending
          ? "Rental agreement signed. Payment submitted and waiting on confirmation — nothing for the patient to do."
          : "Rental agreement signed and payment complete."}
      </Alert>
    );
  }

  const outstanding = [
    needsAgreement && "has not signed the rental agreement",
    needsPayment && "has not yet paid",
  ].filter(Boolean);

  const login = loginSentence(email, phone);

  return (
    <Alert severity="warning">
      <Typography variant="body2" gutterBottom>
        Patient {outstanding.join(" and ")}.
      </Typography>
      {paymentPending && (
        <Typography variant="body2" gutterBottom>
          A payment has been submitted and is waiting on confirmation.
        </Typography>
      )}
      {/*
       * With neither identity on file the patient cannot get into the portal at
       * all, so pointing them at it would be an instruction that fails. Say the
       * real blocker instead.
       */}
      {login ? (
        <Typography variant="body2">
          Direct the patient to{" "}
          <Link href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
            {PORTAL_URL}
          </Link>{" "}
          to complete the process. {login}
        </Typography>
      ) : (
        <Typography variant="body2">
          We have no email or phone on file for this patient, so they cannot log
          in to the portal — contact Patient Care to get one added.
        </Typography>
      )}
    </Alert>
  );
}

export default PatientPortalStatus;
